// oxlint-disable typescript-eslint/prefer-readonly-parameter-types -- Storage is an external mutable DOM interface
// Cross-page persistence for a user-triggered session recording.
//
// The rrweb recorder lives in the host page's JavaScript context, so a full
// navigation tears it down along with every event it held in memory. A user
// who starts a recording, reproduces their bug across two pages, then stops
// would otherwise hand back an empty capture – the recording looks like it
// never started.
//
// Think of it as a relay race: each page load runs one leg, hands its events
// to `sessionStorage`, and the next page load picks the baton up. The marker
// says a race is in progress; the chunks are the legs already run.
//
// `sessionStorage` (not `localStorage`) is deliberate: the recording belongs
// to one tab and must not leak into a second tab or survive the tab closing.

import type { ReplayPrivacySettings } from "@weloop/core/feedback/replay-masking";

import { parseReplayPrivacy } from "@weloop/core/feedback/replay-masking";

export interface UserReplayContext {
	// The thread the recording was started from, when the ask came from an
	// in-thread replay gate. Absent for a recording started from the
	// composer's Session-record button.
	readonly feedbackId?: string;
	readonly messageId?: string;
}

export interface UserReplaySession {
	// Storage keys of the legs already handed over, oldest first.
	readonly chunkKeys: readonly string[];
	readonly context: UserReplayContext | undefined;
	readonly id: string;
	// Sequence for the next leg's key. It only ever counts up: deriving the
	// key from `chunkKeys.length` instead would reuse the key of a leg that
	// was trimmed away and overwrite a leg still in the recording.
	readonly nextChunk: number;
	// The masking the recording started with. Every later page load records
	// its leg with the same settings, so a navigation never loosens them.
	readonly privacy: ReplayPrivacySettings;
	// The project whose widget started the recording. A page load mounted for
	// another project discards it, so its events never reach that project.
	readonly projectId: string | undefined;
	readonly startedAt: number;
}

const MARKER_KEY = "weloop-user-replay";
const CHUNK_PREFIX = "weloop-user-replay-chunk:";

// Browsers give an origin roughly 5 MB of session storage. Staying well under
// it leaves room for whatever else the host page keeps there – a recording
// that fills the quota would break the host site, which is never an
// acceptable trade for a few more seconds of replay.
const MAX_STORED_CHARS = 2_000_000;

/**
 * How long a capture marker outlives its start. A duplicated or restored tab
 * copies `sessionStorage`, so without an expiry a stale marker would bring
 * the Stop bar back on every later visit.
 */
export const CAPTURE_MARKER_TTL_MS = 3 * 60 * 60 * 1000;

const isRecord = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

const parseContext = (raw: unknown): UserReplayContext | undefined => {
	if (!isRecord(raw)) {
		return undefined;
	}
	const { feedbackId, messageId } = raw;
	const context: UserReplayContext = {
		...(typeof feedbackId === "string" ? { feedbackId } : {}),
		...(typeof messageId === "string" ? { messageId } : {}),
	};
	return Object.keys(context).length === 0 ? undefined : context;
};

const parseChunkKeys = (raw: unknown): string[] =>
	Array.isArray(raw)
		? raw.filter((key): key is string => typeof key === "string")
		: [];

/** Read the in-progress recording, if this tab has one. */
export const readReplaySession = (
	storage: Storage,
): UserReplaySession | undefined => {
	try {
		const raw = storage.getItem(MARKER_KEY);
		if (raw === null) {
			return undefined;
		}
		const parsed: unknown = JSON.parse(raw);
		if (!isRecord(parsed)) {
			return undefined;
		}
		const { id, nextChunk, projectId, startedAt } = parsed;
		if (typeof id !== "string" || typeof startedAt !== "number") {
			return undefined;
		}
		const chunkKeys = parseChunkKeys(parsed["chunkKeys"]);
		if (Date.now() - startedAt > CAPTURE_MARKER_TTL_MS) {
			for (const key of chunkKeys) {
				storage.removeItem(key);
			}
			storage.removeItem(MARKER_KEY);
			return undefined;
		}
		return {
			chunkKeys,
			context: parseContext(parsed["context"]),
			id,
			nextChunk: typeof nextChunk === "number" ? nextChunk : chunkKeys.length,
			privacy: parseReplayPrivacy(parsed["privacy"]),
			projectId: typeof projectId === "string" ? projectId : undefined,
			startedAt,
		};
	} catch {
		// A host page can block storage entirely (sandboxed iframe, privacy
		// mode). Recording then degrades to single-page, which is what it did
		// before this module existed.
		return undefined;
	}
};

const writeMarker = (storage: Storage, session: UserReplaySession): boolean => {
	try {
		storage.setItem(MARKER_KEY, JSON.stringify(session));
		return true;
	} catch {
		// Best effort – the in-memory sink still holds this page's events.
		return false;
	}
};

/** Drop every trace of the current recording. */
export const clearReplaySession = (storage: Storage): void => {
	const session = readReplaySession(storage);
	try {
		for (const key of session?.chunkKeys ?? []) {
			storage.removeItem(key);
		}
		storage.removeItem(MARKER_KEY);
	} catch {
		// Best effort.
	}
};

/** Open a new recording, discarding any earlier one still on record. */
export const beginReplaySession = (
	storage: Storage,
	session: Omit<UserReplaySession, "chunkKeys" | "nextChunk">,
): UserReplaySession => {
	clearReplaySession(storage);
	const opened: UserReplaySession = { ...session, chunkKeys: [], nextChunk: 0 };
	writeMarker(storage, opened);
	return opened;
};

const storedLength = (storage: Storage, key: string): number =>
	storage.getItem(key)?.length ?? 0;

/**
 * Hand this page load's leg over to the next page load. Each page load has
 * one leg: pass the key a previous call returned and the leg is replaced
 * whole, so every stored leg starts with the page's own rrweb full snapshot
 * and replays on its own.
 *
 * Returns the leg's key once it is stored, or `undefined` when it is not –
 * no recording in progress, a leg too big for the cap, or a full storage.
 * The caller keeps the events in memory either way.
 */
export const storeReplayLeg = (
	storage: Storage,
	events: readonly unknown[],
	key?: string,
): string | undefined => {
	const session = readReplaySession(storage);
	if (session === undefined || events.length === 0) {
		return undefined;
	}
	try {
		const payload = JSON.stringify(events);
		// Checked before writing: a leg that filled the quota would make the
		// host page's own writes throw. One that cannot fit even alone stays in
		// memory and leaves the older legs alone.
		if (payload.length > MAX_STORED_CHARS) {
			return undefined;
		}
		const legKey = key ?? `${CHUNK_PREFIX}${session.id}:${session.nextChunk}`;
		const olderKeys = session.chunkKeys.filter((stored) => stored !== legKey);
		let total = olderKeys.reduce(
			(sum, stored) => sum + storedLength(storage, stored),
			payload.length,
		);
		// Oldest legs drop first. Each leg replays on its own, so the user only
		// loses the earliest pages of a very long recording.
		while (total > MAX_STORED_CHARS) {
			const oldest = olderKeys.shift();
			if (oldest === undefined) {
				break;
			}
			total -= storedLength(storage, oldest);
			storage.removeItem(oldest);
		}
		storage.setItem(legKey, payload);
		const isListed = writeMarker(storage, {
			...session,
			chunkKeys: [...olderKeys, legKey],
			nextChunk: key === undefined ? session.nextChunk + 1 : session.nextChunk,
		});
		if (!isListed) {
			storage.removeItem(legKey);
			return undefined;
		}
		return legKey;
	} catch {
		// Quota exceeded or storage blocked. The recording keeps running on
		// this page's in-memory events.
		return undefined;
	}
};

/**
 * Replay the handed-over legs, oldest first. `skipKey` leaves out the current
 * page's own leg, which the caller still holds in memory in full.
 */
export const readReplayEvents = (
	storage: Storage,
	skipKey?: string,
): unknown[] => {
	const session = readReplaySession(storage);
	if (session === undefined) {
		return [];
	}
	const events: unknown[] = [];
	for (const key of session.chunkKeys) {
		if (key === skipKey) {
			continue;
		}
		try {
			const raw = storage.getItem(key);
			if (raw === null) {
				continue;
			}
			const parsed: unknown = JSON.parse(raw);
			if (Array.isArray(parsed)) {
				events.push(...(parsed as unknown[]));
			}
		} catch {
			// A corrupt chunk drops out; the rest of the recording survives.
		}
	}
	return events;
};
